import { defineComponent as _defineComponent } from 'vue'
import { DrawPlankColor } from '@/data/plank'
import type { PartType } from '@/partTypes'
import store from '@/store'
import { Plank } from '@/util/canvas/drawPlank'
import Scene from '@/util/dealPlankExtraDraw'
import { plankDrawFactory } from '@/util/drawPlank'
import { computed, onMounted, ref, watch } from 'vue'

import { transformData } from '../../util/transformData'

interface ErrorInfoDataType {
  errorInfo: string[]
  ggid: string
  stockKey: string
  priority: number
}
type ActiveDataType = PartType & ErrorInfoDataType
// 当前选中板件的索引

export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    visible: { type: Boolean, default: false },
    title: { default: '孔槽无匹配刀具详情' },
    data: { default: () => [] }
  },
  emits: ["update:visible", "searchPart"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:visible', val: boolean): void
  (e: 'searchPart', partName: string, index: number, type: 'knivesErr'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { visible: boolean, title: string, data: Array<ErrorInfoDataType> }

const finalDrawData = computed(() => store.state.finalDrawData)




// 抽屉显示/隐藏
watch(
  () => props.visible,
  (val) => {
    if (val) {
      activeIndex.value = 0
      if (!scene.value) {
        scene.value = new Scene('canvas', canvasState.value)
      }
      canvasRef.value?.addEventListener('wheel', handleMousewheel)
      emit('searchPart', activeData.value?.partUniqueId || '', 0, 'knivesErr')
      scene.value?.paint(draw)
      draw()
    } else {
      // 移除监听事件
      canvasRef.value?.removeEventListener('wheel', handleMousewheel)
    }
  }
)

const visible = computed({
  get() {
    return props.visible
  },
  set(val) {
    emit('update:visible', val)
    return true
  },
})
const activeIndex = ref(0)
// 当前选中的板件
const activeData = computed<ActiveDataType | null>(() => {
  // 获取到当前小板所在的大板的信息
  const curPartInfo = props.data[activeIndex.value]
  if (!curPartInfo?.errorInfo) return null
  const stockKeyArr = curPartInfo?.stockKey?.split(':')
  stockKeyArr.pop()
  // 获取所在类别的大板
  const planks = finalDrawData.value
    .filter((plank) => {
      const { texture, matCode, thick } = plank
      const stock = `${texture}:${matCode}:${thick}`
      return stock === stockKeyArr.join(':')
    })
    ?.map((plank) => plank.data)
    .flat()

  if (!planks) return null
  // 精确大板数据
  const curPlanks = planks.find(
    (plank: any) => plank.stockKey === curPartInfo?.stockKey
  )
  // 查找到当前小板的具体的信息
  const curPart = curPlanks.parts.find(
    (part: any) => part.priority === curPartInfo?.priority
  )
  if (!curPart) return null
  return {
    ...curPart,
    ...curPartInfo,
  }
})

const mainRef = ref<HTMLDivElement | null>(null)

// 关闭弹窗
const onClose = () => {
  visible.value = false
}

// 图样
const canvasState = ref({
  width: 450,
  height: 500,
})
const canvasRef = ref<HTMLCanvasElement | null>(null)
const zoomPercent = ref(100) //缩放百分比

// 切换板件
const handleToggle = (type: 'prev' | 'next') => {
  if (type === 'prev') {
    if (activeIndex.value === 0) {
      activeIndex.value = props.data.length - 1
    } else {
      activeIndex.value--
    }
  } else {
    if (activeIndex.value === props.data.length - 1) {
      activeIndex.value = 0
    } else {
      activeIndex.value++
    }
  }
  scene.value?.reset()
  draw()
  zoomPercent.value = 100
  emit('searchPart', activeData.value?.partUniqueId || '', 0, 'knivesErr')
}

// 监听键盘事件
const handleKeydown = (e: KeyboardEvent) => {
  // console.log('键盘事件', e.key)
  // if (e.key === 'ArrowRight') {
  //   handleToggle('next')
  // } else if (e.key === 'ArrowLeft') {
  //   handleToggle('prev')
  // }
}
const ctx = ref<CanvasRenderingContext2D | null>(null)
const startPosition = ref({
  startX: 0,
  startY: 0,
})
// 绘制
const draw = (isMove = false) => {
  if (!ctx.value) ctx.value = canvasRef.value?.getContext('2d')!
  if (!activeData.value) return
  ctx.value?.clearRect(0, 0, canvasState.value.width, canvasState.value.height)

  const partTemp = transformData(activeData.value, store)

  const { rect } = partTemp
  // 获取小板的宽高
  const { width, height } = rect

  // 容器的宽高
  const [containerWidth, containerHeight] = [
    canvasState.value.width,
    canvasState.value.height,
  ]

  // 根据容器宽高和小板的宽高计算出缩放比例
  const containerRate = Math.min(
    containerWidth / width,
    containerHeight / height
  ).toFixed(2)

  // 板件宽高比
  const partRate = Math.min(width / height, height / width).toFixed(2)

  let scaleRate = Math.min(Number(containerRate), Number(partRate))
  if (scaleRate > 0.4) {
    scaleRate -= 0.1
  }
  const scale = 1 / scaleRate

  // 计算小板的绘绘制起点，居中绘制
  let startX = Math.abs(containerWidth - width * scaleRate) / 2
  let startY = Math.abs(containerHeight - height * scaleRate) / 2

  if (!isMove) {
    startPosition.value = {
      startX,
      startY,
    }
  } else {
    startX = startPosition.value.startX
    startY = startPosition.value.startY
  }

  const draws = plankDrawFactory(
    ctx.value!,
    partTemp,
    startX,
    startY,
    scale,
    1,
    undefined,
    ['plankSize']
  )
  const draw1 = new Plank(
    ctx.value!,
    partTemp,
    startX,
    startY,
    scale,
    1,
    undefined
  )
  draw1.addStrategy(
    Object.keys(draws).map(
      (it) => draws[it as keyof ReturnType<typeof plankDrawFactory>]
    )
  )
  draw1.setFillStyle(DrawPlankColor.normal)
  draw1.draw()
}

const scene = ref<InstanceType<typeof Scene> | null>(null)
function handleZoom(num: number, e?: MouseEvent) {
  zoomPercent.value = +zoomPercent.value + num
  if (zoomPercent.value <= 50) {
    zoomPercent.value = 50
    return
  }

  if (zoomPercent.value >= 500) {
    zoomPercent.value = 500
    return
  }
  num > 0 ? scene.value?.zoomIn() : scene.value?.zoomOut()
  scene.value?.paint(draw)
}

const handleMousewheel = (e: WheelEvent) => {
  e.preventDefault()
  let z = e.deltaY > 0 ? -10 : 10
  handleZoom(z)
}

onMounted(() => {
  ctx.value = canvasRef.value?.getContext('2d')!
})

return { __sfc: true,finalDrawData, props, emit, visible, activeIndex, activeData, mainRef, onClose, canvasState, canvasRef, zoomPercent, handleToggle, handleKeydown, ctx, startPosition, draw, scene, handleZoom, handleMousewheel }
}

})